import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StorefrontLayout } from '@/components/layout/storefront-layout';
import { AccountLayout } from '@/components/layout/account-layout';
import { HomePage } from '@/pages/home';
import { CatalogPage } from '@/pages/catalog';
import { ProductPage } from '@/pages/product';
import { ServiceLandingPage } from '@/pages/service-landing';
import { CartPage } from '@/pages/cart';
import { CheckoutPage } from '@/pages/checkout';
import { LoginPage, RegisterPage, ForgotPasswordPage } from '@/pages/auth';
import {
  AccountOrdersPage,
  AccountProfilePage,
  AccountSecurityPage,
  AccountFavoritesPage,
  AccountCouponsPage,
  AccountSupportPage,
} from '@/pages/account';

const AdminLayout = lazy(() =>
  import('@/components/layout/admin-layout').then((m) => ({ default: m.AdminLayout })),
);
const AdminDashboardPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminDashboardPage })),
);
const AdminProductsPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminProductsPage })),
);
const AdminOrdersPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminOrdersPage })),
);
const AdminCustomersPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminCustomersPage })),
);
const AdminCouponsPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminCouponsPage })),
);
const AdminSettingsPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminSettingsPage })),
);
const AdminUsersPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminCmsPage = lazy(() => import('@/pages/admin').then((m) => ({ default: m.AdminCmsPage })));
const AdminAnalyticsPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminAnalyticsPage })),
);
const AdminMarketingPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminMarketingPage })),
);
const AdminSupportPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminSupportPage })),
);

function Lazy({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="p-8 text-muted">Carregando…</div>}>{children}</Suspense>
  );
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

const router = createBrowserRouter([
  {
    element: <StorefrontLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'catalogo', element: <CatalogPage /> },
      { path: 'instagram', element: <ServiceLandingPage landingKey="instagram" /> },
      {
        path: 'instagram/seguidores',
        element: <ServiceLandingPage landingKey="instagram-seguidores" />,
      },
      {
        path: 'instagram/curtidas',
        element: <ServiceLandingPage landingKey="instagram-curtidas" />,
      },
      { path: 'tiktok', element: <ServiceLandingPage landingKey="tiktok" /> },
      {
        path: 'tiktok/seguidores',
        element: <ServiceLandingPage landingKey="tiktok-seguidores" />,
      },
      {
        path: 'tiktok/curtidas',
        element: <ServiceLandingPage landingKey="tiktok-curtidas" />,
      },
      { path: 'produto/:slug', element: <ProductPage /> },
      { path: 'carrinho', element: <CartPage /> },
      { path: 'checkout', element: <CheckoutPage /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'cadastro', element: <RegisterPage /> },
      { path: 'recuperar-senha', element: <ForgotPasswordPage /> },
      {
        path: 'conta',
        element: <AccountLayout />,
        children: [
          { index: true, element: <Navigate to="pedidos" replace /> },
          { path: 'pedidos', element: <AccountOrdersPage /> },
          { path: 'perfil', element: <AccountProfilePage /> },
          { path: 'seguranca', element: <AccountSecurityPage /> },
          { path: 'favoritos', element: <AccountFavoritesPage /> },
          { path: 'cupons', element: <AccountCouponsPage /> },
          { path: 'suporte', element: <AccountSupportPage /> },
        ],
      },
    ],
  },
  {
    path: 'admin',
    element: (
      <Lazy>
        <AdminLayout />
      </Lazy>
    ),
    children: [
      {
        index: true,
        element: (
          <Lazy>
            <AdminDashboardPage />
          </Lazy>
        ),
      },
      {
        path: 'produtos',
        element: (
          <Lazy>
            <AdminProductsPage />
          </Lazy>
        ),
      },
      {
        path: 'pedidos',
        element: (
          <Lazy>
            <AdminOrdersPage />
          </Lazy>
        ),
      },
      {
        path: 'clientes',
        element: (
          <Lazy>
            <AdminCustomersPage />
          </Lazy>
        ),
      },
      {
        path: 'cupons',
        element: (
          <Lazy>
            <AdminCouponsPage />
          </Lazy>
        ),
      },
      {
        path: 'suporte',
        element: (
          <Lazy>
            <AdminSupportPage />
          </Lazy>
        ),
      },
      {
        path: 'configuracoes',
        element: (
          <Lazy>
            <AdminSettingsPage />
          </Lazy>
        ),
      },
      {
        path: 'usuarios',
        element: (
          <Lazy>
            <AdminUsersPage />
          </Lazy>
        ),
      },
      {
        path: 'cms',
        element: (
          <Lazy>
            <AdminCmsPage />
          </Lazy>
        ),
      },
      {
        path: 'analytics',
        element: (
          <Lazy>
            <AdminAnalyticsPage />
          </Lazy>
        ),
      },
      {
        path: 'marketing',
        element: (
          <Lazy>
            <AdminMarketingPage />
          </Lazy>
        ),
      },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
